import type { ZodTypeAny } from 'zod';

import type { RequestInput } from '../types';
import { HttpMethod, MIME_TYPE } from '../types';
import { urlWithLocale } from './locale';
import { evaluateSession } from './handleSession';
import { logger } from './logger';
import { injectOktaToken } from '../oktaTokenInject';

const validationCheck = (
    zodSchema: ZodTypeAny | undefined,
    url: string,
    status: number,
    responseJson: unknown,
    method: string | HttpMethod = HttpMethod.GET
) => {
    if (zodSchema) {
        const zodResult = zodSchema.safeParse(responseJson);

        if (!zodResult.success) {
            console.error(
                '[validationError]',
                `${method} - ${url.toString()} [${status}]`,
                '\nerrors:',
                JSON.stringify(zodResult.error.errors, null, 2),
                '\nresponse:',
                JSON.stringify(responseJson, null, 2)
            );
        }
    }
};

async function parseResponse<T>({
    response,
    isJson,
    zodSchema,
    url,
    method = HttpMethod.GET,
}: {
    response: Response;
    isJson?: boolean;
    zodSchema?: ZodTypeAny;
    url: string;
    method?: string | HttpMethod;
}) {
    if (isJson) {
        const responseJson = (await response.json()) as T;
        validationCheck(zodSchema, url, response.status, responseJson, method);

        return responseJson;
    }

    return (await response.text()) as T;
}

// Each fetching attempt is triggered and the timeout to validate that the API call does not exceed its own time are here
const attemptFetch = async (
    url: RequestInput,
    timeoutDuration?: number,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    }
): Promise<Response | null> => {
    const controller = new AbortController();
    // when timeoutDuration is defined that means each attempt should have an expiration time
    const abortSignalId = timeoutDuration ? setTimeout(() => controller.abort(), timeoutDuration) : undefined;

    try {
        return await fetch(url, {
            ...init,
            signal: abortSignalId ? controller.signal : init?.signal,
        });
    } catch (error) {
        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                logger.error(
                    '[ >> coreFetch << ] Fetch aborted due to timeout: %d ms for URL: %s, %o',
                    timeoutDuration,
                    url.toString(),
                    init
                );
            } else {
                logger.error('[ >> coreFetch << ] Error occurred during fetch. %o', {
                    error,
                    url: url.toString(),
                    init,
                });
            }
        } else {
            // This is probably no longer needed
            logger.error('[ >> coreFetch << ] Unknown error occurred during fetch. %s, %o ', url.toString(), init);
        }

        return null;
    } finally {
        clearTimeout(abortSignalId);
    }
};

// Helper function to prepare request parameters
const prepareRequest = (
    input: RequestInput,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    }
) => {
    const url = init?.withLocale ? urlWithLocale(input) : input;
    const additionalSuccessCodes = init?.additionalSuccessCodes ?? [];
    return { url, additionalSuccessCodes };
};

// Handle retries logic -- Loop to re-try a new fetch
const handleRetries = async (
    url: RequestInfo,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    },
    numberOfRetries?: number,
    timeoutDuration?: number
) => {
    let response: Response | null = null;

    const attempts = numberOfRetries || 0;
    // if the number of numberOfRetries is defined it time duration should have a default value of 3 seconds
    const attemptDuration = attempts > 0 && timeoutDuration === undefined ? 3000 : timeoutDuration;
    for (let attempt = 0; attempt <= attempts; attempt++) {
        response = await attemptFetch(url, attemptDuration, init);
        if (response) break;

        logger.info('[ >> coreFetch << ] Retrying fetch... Attempt %d for URL: %s, %o', attempt, url.toString(), init);
    }

    if (!response) {
        logger.error(
            '[ >> coreFetch << ] coreFetch failed after %d attempts for URL: %s, %o',
            numberOfRetries,
            url.toString(),
            init
        );
    }

    return response;
};

// Parse and validate response
const parseAndValidateResponse = async <T>(
    response: Response,
    zodSchema: ZodTypeAny | undefined,
    url: string,
    init?: RequestInit
) => {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType?.includes(MIME_TYPE.json);
    return await parseResponse<T>({
        response,
        isJson,
        zodSchema,
        url,
        method: init?.method,
    });
};

// Handle response status and return parsed data or error
const handleResponseCode = <T>(response: Response, parsedResponse: T, additionalSuccessCodes: number[]): T | null => {
    if (response.status === 204) {
        // HTTP 204 means a successful empty response
        return null;
    }
    const isSuccess =
        (response.status >= 200 && response.status < 300) || additionalSuccessCodes.includes(response.status);
    if (isSuccess) return parsedResponse;

    logger.error('[ >> coreFetch << ] %o %o', response, parsedResponse);

    return null;
};

// Handle response processing
const processFetchResponse = async <T>({
    fetchResponse,
    url,
    init,
    zodSchema,
    additionalSuccessCodes,
}: {
    fetchResponse: Response | null;
    url: string;
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    };
    zodSchema?: ZodTypeAny;
    additionalSuccessCodes: number[];
}): Promise<T | null> => {
    if (!fetchResponse) return null;

    try {
        const parsedResponse = await parseAndValidateResponse<T>(fetchResponse, zodSchema, url, init);

        return handleResponseCode(fetchResponse, parsedResponse, additionalSuccessCodes);
    } catch (error) {
        if (error instanceof Error) {
            logger.error(
                '[ >> coreFetch << ] Error processing fetch response: %s, %o, %d',
                error.message,
                { stack: error.stack },
                additionalSuccessCodes
            );
        } else {
            logger.error('[ >> coreFetch << ] Unknown error occurred during fetch. %d', additionalSuccessCodes);
        }

        return null;
    }
};

/**
 * @deprecated since `libs/core/src/utils/apiFetch.ts` is created to allow passing backend errors to the UI instead of null
 */
export const coreFetch = async <T>(
    input: RequestInput,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    },
    zodSchema?: ZodTypeAny,
    numberOfRetries?: number,
    timeoutDuration?: number
): Promise<T | null> => {
    const finalInit = await injectOktaToken(init);

    const { url, additionalSuccessCodes } = prepareRequest(input, finalInit);

    // Is Client Side?
    if (typeof window !== 'undefined') {
        await evaluateSession();
    }

    const fetchResponse = await handleRetries(url.toString(), finalInit, numberOfRetries, timeoutDuration);

    return processFetchResponse<T>({
        fetchResponse,
        url: url.toString(),
        init,
        zodSchema,
        additionalSuccessCodes,
    });
};
